import { 
  DateTime, 
  Duration 
} from 'luxon';
import inputTemplate from 'bundle-text:./templates/input-template.html';
import { 
  handleDateBackspace, 
  handleDateBlur, 
  handleDateInput 
} from './date-input-handler.mjs';

export class UIManager {
  /**
   * @param {AppState} appState
   */
  constructor(appState) {
    /** @type {AppState} */
    this.appState = appState;
    /** @type {View | null} */
    this.view = null;
    /** @type {HTMLCanvasElement | null} */
    this.canvas = null;
    /** @type {import('./chart.mjs').ChartComponent | null} */
    this.chartComponentInstance = null;

    /** @type {UISelectors} */
    this.selectors = Object.freeze({
      table: '#input-wrapper table',
      form: '#input-wrapper form',
      dateInput: '#input-date-text',
      periodInput: '#input-period',
      specialInput: '#input-special',
      demoButton: '#demo',
      footer: '#footer',
      chartCanvas: '#chart-canvas'
    });
  }

  /**
   * Fylder en HTML <select> dropdown ud fra de konfigurerbare perioder i staten
   * @param {HTMLSelectElement} selectElement
   * @returns {void}
   */
  populateInputTemplate(selectElement) {
    const periods = this.appState.getConfig('periods');

    for (let i=1; i < periods.length; i++) {
      if (!periods[i]) continue;

      const option = document.createElement('option');
      option.setAttribute('value', String(i));
      option.textContent = Duration.fromObject(periods[i] || {})?.toHuman({ unitDisplay: "short" });
      selectElement.append(option);
    }
  }

  /**
   * Genererer og initialiserer visningen samt injects HTML-template i DOM'en
   * @returns {View | null} Bundtede DOM-referencer (view)
   */
  createView() {
    const inputWrapper = document.querySelector('#input-wrapper');
    if (!inputWrapper) {
      console.error('[UI] Kunne ikke finde #input-wrapper i DOM.');
      return null;
    }
  
    const template = document.createElement('template');
    template.innerHTML = inputTemplate;
    const templateClone = /** @type {DocumentFragment} */ (template.content.cloneNode(true));

    const selectElement = /** @type {HTMLSelectElement | null} */ (templateClone.querySelector('select#input-period'));
    if (selectElement) {
      this.populateInputTemplate(selectElement)
    }

    inputWrapper.append(templateClone);

    this.view = {
      form: /** @type {HTMLFormElement | null} */ (inputWrapper.querySelector(this.selectors.form)),
      dateInput: /** @type {HTMLInputElement | null} */ (inputWrapper.querySelector(this.selectors.dateInput)),
      periodInput: /** @type {HTMLSelectElement | HTMLInputElement | null} */ (inputWrapper.querySelector(this.selectors.periodInput)),
      specialInput: /** @type {HTMLInputElement | null} */ (inputWrapper.querySelector(this.selectors.specialInput)),
      demoButton: /** @type {HTMLButtonElement | null} */ (inputWrapper.querySelector(this.selectors.demoButton)),
      table: /** @type {HTMLTableElement | null} */ (inputWrapper.querySelector(this.selectors.table)),
      footer: /** @type {HTMLElement | null} */ (inputWrapper.querySelector(this.selectors.footer)),
      chartWrapper: /** @type {HTMLElement | null} */ (document.querySelector('#chart-wrapper')),
    };

    if (this.view.chartWrapper) {
      this.view.chartWrapper.classList.add(
        'column',
        'is-three-quarters-desktop',
        'is-mobile'
      );
    }

    this.canvas = /** @type {HTMLCanvasElement | null} */ (document.querySelector(this.selectors.chartCanvas));

    return this.view;
  }

  /**
   * Modtager færdige beregningsdata fra controlleren og opdaterer samtlige skærmelementer
   * @param {EntryList} entries - De rå punkter fra staten
   * @param {CompleteRecord | null} calculationResult - Det færdige resultat fra Calculator (fx med finalExpiry og points)
   * @param {EntryDeleteCallback} onDeleteCallback - Slette-callback sendt fra controlleren
   * @returns {Promise<void>}
   */
  async syncView(entries, calculationResult, onDeleteCallback) {
    if (!this.view || !this.canvas) return;

    // 1. Tegn tabellen baseret på de entries, der ligger i staten lige nu
    // Send et callback-arrow ind, så 'this' forbliver bundet til UIManager instansen
    this.renderTable(entries, onDeleteCallback);

    // 2. Clear chart if there are no calculation results/entries
    if (!calculationResult || !entries.length) {
      this.destroyChart();
      this.setFooter(null);
      this.setDemoButton(false);
      return;
    }

    // 3. Håndter asynkron indlæsning og optegning af diagrammet
    try {
      if (!this.chartComponentInstance) {
        const { ChartComponent } = await import('./chart.mjs');
        this.chartComponentInstance = new ChartComponent(this.appState);
      }
      this.chartComponentInstance?.render(this.canvas, calculationResult);
    } catch (error) {
      console.error('Kunne ikke indlæse eller tegne diagrammet:', error);
    }
    
    // 4. Opdater bundtekst og demo-knap
    this.setFooter(calculationResult.finalExpiry);
    this.setDemoButton(this.appState.demoActive);
  }

  /**
   * Binder alle event listeners til DOM-elementerne.
   * Modtager callbacks fra app.mjs for at holde forretningslogikken adskilt fra UI.
   * @param {SyncCallback} onAddEntry
   * @param {EntryDeleteCallback} onRemoveEntry
   * @param {SyncCallback} onToggleDemo
   * @param {SyncCallback} onPageShowSync
   * @returns {void}
   */
  bindEvents(onAddEntry, onRemoveEntry, onToggleDemo, onPageShowSync) {
    if (!this.view) return;
    
    // 1. Submit-formular
    this.view.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      onAddEntry();
    });

    // 2. Løbende input-validering og formatering af dato
    this.view.dateInput?.addEventListener('keydown', handleDateBackspace);
    this.view.dateInput?.addEventListener('input', handleDateInput);

    // 3. Formatering af dato, når feltet mister fokus (blur)
    this.view.dateInput?.addEventListener('blur', handleDateBlur);

    // 4. Klik på Demo-knappen
    this.view.demoButton?.addEventListener('click', () => onToggleDemo());

    // 5. Print-håndtering
    window.addEventListener('beforeprint', () => {
      this.view?.chartWrapper?.classList.add('chart-width-on-print');
      this.chartComponentInstance?.changeAlphaColor('print');
    });

    window.addEventListener('afterprint', () => {
      this.view?.chartWrapper?.classList.remove('chart-width-on-print');
      this.chartComponentInstance?.changeAlphaColor('screen');
    });

    // 6. Sidelivscyklus (Page Lifecycle)
    window.addEventListener('pagehide', () => this.destroyChart());

    window.addEventListener('pageshow', (event) => {
      if (event.persisted === true) {
        onPageShowSync();
      }
    });
  }

  /**
   * Tegner tabellen over registrerede entries i DOM'en
   * @param {EntryList} entries 
   * @param {EntryDeleteCallback} onDelete
   * @returns {void}
   */
  renderTable(entries, onDelete) {
    if (!this.view || !this.view.table || !this.view.footer) return;

    const { table, footer } = this.view;
    table.tBodies[0]?.remove();

    if (!entries.length) {
      table.classList.add('is-hidden');
      footer.textContent = '';
      return;
    }

    table.classList.remove('is-hidden');
    const tbody = table.createTBody();

    [...entries]
      .sort((a, b) => a.date.toMillis() - b.date.toMillis())
      .forEach((entry, index) => {
        const row = tbody.insertRow();
        row.dataset.index = String(index);

        const deleteCell = row.insertCell();
        const dateCell = row.insertCell();
        const periodCell = row.insertCell();
        const specialCell = row.insertCell();
        const expiryCell = row.insertCell();

        const deleteButton = document.createElement('button');
        deleteButton.type = 'button';
        deleteButton.classList.add('delete');
        deleteButton.setAttribute('aria-label', 'Slet forhold');
        // Kalder automatisk det medsendte removeEntry callback
        deleteButton.addEventListener('click', () => onDelete(entry));
        deleteCell.classList.add('narrow-cell', 'delete-cell', 'px-1');
        deleteCell.append(deleteButton);

        const dateContainer = document.createElement('span');
        dateContainer.classList.add('icon-text');
        dateContainer.append(document.createTextNode(
          entry.date.toLocaleString(DateTime.DATE_MED),
        ));
        
        dateCell.append(dateContainer);

        periodCell.textContent = Duration.fromObject(entry.len).toHuman({ unitDisplay: "short" });

        specialCell.classList.add('narrow-cell', 'px-1');
        
        if (entry.special) {
          const specialIcon = document.createElement('i');
          specialIcon.classList.add('fa-solid', 'fa-car', 'has-text-grey');
          const specialContainer = document.createElement('span');
          specialContainer.classList.add('icon');
          specialContainer.append(specialIcon);
          specialCell.append(specialContainer);
        }

        const expiryContainer = document.createElement('span');
        expiryContainer.classList.add('icon-text', 'level');

        const expiryLeft = document.createElement('span');
        expiryLeft.classList.add('level-left');

        const expiryValue = document.createElement('span');
        expiryValue.classList.add('level-item');
        expiryValue.textContent = entry.date
          .plus(entry.len)
          .toLocaleString(DateTime.DATE_MED);

        expiryLeft.append(expiryValue);
        expiryContainer.append(expiryLeft);
        expiryCell.append(expiryContainer);
      });
  }

  /**
   * Opdaterer sidens bundtekst med karensudløb
   * @param {DateTime|null} finalExpiry 
   * @returns {void}
   */
  setFooter(finalExpiry) {
    if (!this.view || !this.view.footer) return;
    this.view.footer.textContent = finalExpiry
      ? `Karensudløb: ${finalExpiry.toLocaleString(DateTime.DATE_FULL)}`
      : '';
  }

  /**
   * Skifter tekstindhold på demo-knappen alt efter appens tilstand
   * @param {boolean} active 
   * @returns {void}
   */
  setDemoButton(active) {
    if (!this.view || !this.view.demoButton) return;
    this.view.demoButton.textContent = active ? 'Ryd' : 'Demo';
  }

  /**
   * Nulstiller formularfelter og flytter fokus til dato-inputtet
   * @returns {void}
   */
  clearForm() {
    if (!this.view) return;
    this.view.form?.reset();
    this.view.dateInput?.focus();
    this.view.dateInput?.classList.remove('has-background-danger-light');
  }



  /**
   * Oprydning af chart-instansen
   * @returns {void}
   */
  destroyChart() {
    if (this.chartComponentInstance) {
      console.debug('[UI] Siden skjules. Destruerer diagrammet for at frigive hukommelse...');
      this.chartComponentInstance.destroy();
      this.chartComponentInstance = null; 
    }
  }
}